import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CreateNewCard,
  CreateNewCardHolder,
  Icon,
  Modal,
} from '@/components/generic';
import { DiscountType } from '@/types/coupon';
import { copyToClipboard } from '@/utils/string';
import {
  TCoupon,
  useGetCardTypes,
  useGetCurrentBusinessInfo,
  useGetTiers,
} from '@xhype/card-app-sdk';
import {
  useGetCardHolderData,
  useGetCards,
  useGetMultiInfoCard,
  useGetOnchainUserTxs,
} from '@package/smart-wallet/hooks';
import { useGetUserCoupons } from '@package/smart-wallet/hooks/coupon/useGetUserCoupons';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Coupons = () => {
  const { t } = useTranslation();
  const { coupons } = useGetUserCoupons();
  const router = useNavigate();
  const [isModalCreateCardOpen, setIsModalCreateCardOpen] = useState(false);
  const [isModalOpenCreateCardHolder, setIsModalOpenCreateCardHolder] =
    useState(false);
  const [selectedCardType, setSelectedCardType] = useState<any>(null);

  // Get cards data
  const { cardsData } = useGetCards();

  // Get card holder data
  const { cardHolderInfo } = useGetCardHolderData();

  // Get transactions to check for pending ones
  const { records: transactionsData } = useGetOnchainUserTxs({
    pageNum: 1,
  });

  // Get card types
  const { data: cardTypes } = useGetCardTypes({
    enabled: true,
  });

  // Get multi card info
  const cardIds = useMemo(() => {
    if (cardsData) {
      const ids = cardsData.map((card: any) => Number(card?.id));
      if (ids.length > 0) {
        return ids;
      }
    }
    return [0];
  }, [cardsData]);

  const { cardsData: MulticardsData } = useGetMultiInfoCard({ cardIds });

  // Clean up sessionStorage and states on page refresh/mount
  useEffect(() => {
    // Clear all coupon-related data from sessionStorage
    sessionStorage.removeItem('pendingCouponCode');
    sessionStorage.removeItem('pendingCouponCardType');
    sessionStorage.removeItem('couponModalOpened');

    // Reset component states
    setIsModalCreateCardOpen(false);
    setIsModalOpenCreateCardHolder(false);
    setSelectedCardType(null);
  }, []); // Empty dependency array = runs only once on mount

  // Check for pending coupon after card holder creation
  useEffect(() => {
    const pendingCouponCode = sessionStorage.getItem('pendingCouponCode');
    const pendingCardTypeStr = sessionStorage.getItem('pendingCouponCardType');

    if (pendingCouponCode && pendingCardTypeStr && cardHolderInfo) {
      try {
        const pendingCardType = JSON.parse(pendingCardTypeStr);

        // Check if card holder was created for this card type
        const hasCardHolder = cardHolderInfo?.records?.find(
          (record: any) => record?.cardTypeId === pendingCardType?.cardTypeId,
        )?.holderId;

        if (hasCardHolder) {
          // Clear sessionStorage
          sessionStorage.removeItem('pendingCouponCode');
          sessionStorage.removeItem('pendingCouponCardType');

          // Open modal to create card with coupon
          setSelectedCardType({
            ...pendingCardType,
            hasCardHolder,
            preselectedCoupon: pendingCouponCode,
          });
          setIsModalCreateCardOpen(true);
        }
      } catch (error) {
        console.error('Error parsing pending coupon data:', error);
        sessionStorage.removeItem('pendingCouponCode');
        sessionStorage.removeItem('pendingCouponCardType');
      }
    }
  }, [cardHolderInfo]);

  // Get business info to fetch tiers
  const { data: businessInfo } = useGetCurrentBusinessInfo({
    enabled: true,
  });

  const businessId = useMemo(() => {
    if (!businessInfo) return 0;
    return businessInfo?.id;
  }, [businessInfo]);

  const { data: tiersData } = useGetTiers({
    businessId: Number(businessId),
    enabled: !!businessId,
  });

  const activeCoupons = (coupons as TCoupon[]).filter((coupon) => {
    const now = new Date();
    const validUntil = coupon.validUntil ? new Date(coupon.validUntil) : null;
    const hasReachedMaxUses =
      coupon.maxUsesPerUser && coupon.userCoupons
        ? coupon.userCoupons.usedCount >= coupon.maxUsesPerUser
        : false;

    return (
      coupon.isActive && !hasReachedMaxUses && (!validUntil || validUntil > now)
    );
  });

  const usedCoupons = (coupons as TCoupon[]).filter((coupon) => {
    const now = new Date();
    const validUntil = coupon.validUntil ? new Date(coupon.validUntil) : null;
    const hasReachedMaxUses =
      coupon.maxUsesPerUser && coupon.userCoupons
        ? coupon.userCoupons.usedCount >= coupon.maxUsesPerUser
        : false;

    return (
      !coupon.isActive || hasReachedMaxUses || (validUntil && validUntil <= now)
    );
  });

  const formatDiscount = (coupon: TCoupon) => {
    if (coupon.discountType === DiscountType.PERCENTAGE) {
      return `${(coupon.discountValue * 100).toFixed(0)}%`;
    }
    return `$${coupon.discountValue.toFixed(2)}`;
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getCouponTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      CARD_CREATION: t('coupons.type_card_creation'),
      DEPOSIT_BONUS: t('coupons.type_deposit_bonus'),
      DEPOSIT_DISCOUNT: t('coupons.type_deposit_discount'),
    };
    return labels[type] || type;
  };

  const getTierLabel = (tierRestrictions: string | null | undefined) => {
    if (!tierRestrictions) return t('coupons.all_tiers');

    // If we don't have tiers data yet, show the raw value
    if (!tiersData || tiersData.length === 0) return tierRestrictions;

    // If it's a JSON array string, parse it
    try {
      const tierIds = JSON.parse(tierRestrictions);

      if (Array.isArray(tierIds)) {
        // Map tier IDs to tier names
        const tierNames = tierIds
          .map((tierId) => {
            const tier = tiersData.find((t) => t.id === Number(tierId));
            return tier?.name || tierId;
          })
          .filter(Boolean);

        return tierNames.length > 0
          ? tierNames.join(', ')
          : t('coupons.all_tiers');
      }

      if (typeof tierIds === 'string') {
        const tiers = tierIds.split(',').map((id) => id.trim());
        const tierNames = tiers
          .map((tierId) => {
            const tier = tiersData.find((t) => t.id === Number(tierId));
            return tier?.name || tierId;
          })
          .filter(Boolean);

        return tierNames.length > 0
          ? tierNames.join(', ')
          : t('coupons.all_tiers');
      }

      if (typeof tierIds === 'number') {
        const tier = tiersData.find((t) => t.id === tierIds);
        return tier?.name || tierRestrictions;
      }
    } catch {
      // If it's not JSON, try to find it as a single tier ID
      const tier = tiersData.find((t) => t.id === Number(tierRestrictions));

      return tier?.name || tierRestrictions;
    }

    return tierRestrictions;
  };

  // Check if there are pending transactions
  const hasPendingTransactions = useMemo(() => {
    if (!transactionsData || transactionsData.length === 0) return false;
    const pendingStatuses = ['PENDING', 'PROCESSING', 'ONHOLD'];
    return transactionsData.some(
      (transaction: any) =>
        pendingStatuses.includes(transaction.status) &&
        transaction.type === 'CREATE',
    );
  }, [transactionsData]);

  // Get card types for a specific tier restriction
  const getCardTypesForCoupon = useCallback(
    (coupon: TCoupon) => {
      if (!cardTypes || cardTypes.length === 0 || !tiersData) return [];

      // Get tier from coupon restrictions
      let tierIds: number[] = [];
      if (coupon.tierRestrictions) {
        tierIds = coupon.tierRestrictions
          .split(',')
          .map((id) => Number(id.trim()));
      }

      // Get the first tier's card types (or all if no restriction)
      const tierId = tierIds.length > 0 ? tierIds[0] : null;
      if (!tierId) return [];

      const tierRules = tiersData?.find(
        (tier: any) => tier?.id === tierId,
      )?.rules;
      const value =
        tierRules?.find((rule: any) => rule?.type === 'CARD_TYPE')?.value || '';

      let cardTypesArray: number[] = [];
      if (value || value !== '') {
        cardTypesArray = value.split(',').map((v: string) => Number(v.trim()));
      }

      const types = cardTypes
        ?.filter((type) => cardTypesArray?.includes(type?.cardTypeId))
        .map((cardtype) => {
          const hasCardHolder =
            cardHolderInfo?.records?.find(
              (record: any) => record?.cardTypeId === cardtype?.cardTypeId,
            )?.holderId || false;
          let activeCardCount = 0;
          if (hasCardHolder) {
            activeCardCount =
              MulticardsData?.reduce((acc: number, card: any) => {
                if (
                  card?.cardTypeId === cardtype?.cardTypeId &&
                  card?.status !== 'cancel'
                ) {
                  return acc + 1;
                }
                return acc;
              }, 0) || 0;
          }
          return {
            ...cardtype,
            hasCardHolder,
            activeCardCount,
          };
        });
      return types;
    },
    [cardTypes, cardHolderInfo, MulticardsData, tiersData],
  );

  // Handle claiming a CARD_CREATION coupon
  const handleClaimCardCreationCoupon = useCallback(
    (coupon: TCoupon) => {
      if (hasPendingTransactions) {
        return toast.error(t('coupons.pending_card_error'));
      }

      const cardTypesForCoupon = getCardTypesForCoupon(coupon);

      if (!cardTypesForCoupon || cardTypesForCoupon.length === 0) {
        copyToClipboard(coupon.code);
        toast.success(t('coupons.code_copied_no_types'));
        router('/cards/new');
        return;
      }

      // Use the first available card type
      const cardType = cardTypesForCoupon[0];

      // Get tier info from coupon restrictions
      let tierId: number | null = null;
      if (coupon.tierRestrictions) {
        try {
          const parsed = JSON.parse(coupon.tierRestrictions);
          if (Array.isArray(parsed)) {
            tierId = parsed[0];
          } else if (typeof parsed === 'string') {
            tierId = Number(parsed.split(',')[0].trim());
          } else if (typeof parsed === 'number') {
            tierId = parsed;
          }
        } catch {
          tierId = Number(coupon.tierRestrictions);
        }
      }

      const tierSelectedInfo = tiersData?.find(
        (tier: any) => tier.id === tierId,
      );

      // Store coupon info in sessionStorage for use after card holder creation
      sessionStorage.setItem('pendingCouponCode', coupon.code);
      sessionStorage.setItem(
        'pendingCouponCardType',
        JSON.stringify({
          ...cardType,
          tierSelectedInfo,
        }),
      );

      setSelectedCardType({
        ...cardType,
        tierSelectedInfo,
        preselectedCoupon: coupon.code, // Pass the coupon code
      });

      if (cardType?.hasCardHolder) {
        setIsModalCreateCardOpen(true);
      } else {
        setIsModalOpenCreateCardHolder(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPendingTransactions, getCardTypesForCoupon, tiersData],
  );

  return (
    <>
      <Card title={t('coupons.title')} className='m-0'>
        <div className='flex flex-col gap-6 p-5 pt-0 max-lg:px-3 max-w-4xl mx-auto'>
          {activeCoupons.length > 0 && (
            <div className='flex flex-col gap-3'>
              <h3 className='text-sm font-semibold text-t-secondary'>
                {t('coupons.available')}
              </h3>
              {activeCoupons.map((coupon) => {
                return (
                  <div
                    key={coupon.id}
                    className='flex items-center justify-between p-4 rounded-lg bg-b-surface2 border border-b-border hover:border-brand-primary transition-colors'
                  >
                    <div className='flex flex-col gap-1 flex-1'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <span className='font-semibold text-t-primary text-base'>
                          {coupon.code}
                        </span>
                        <span className='px-2 py-0.5 text-xs font-medium rounded-full bg-brand-primary/10 text-brand-primary'>
                          {formatDiscount(coupon)}
                        </span>
                        <span className='px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/10 text-blue-500'>
                          {getCouponTypeLabel(coupon.type)}
                        </span>
                        <span className='px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/10 text-purple-500'>
                          {getTierLabel(coupon.tierRestrictions)}
                        </span>
                      </div>
                      <p className='text-sm text-t-primary font-medium'>
                        {coupon.name}
                      </p>
                      {coupon.description && (
                        <p className='text-sm text-t-secondary'>
                          {coupon.description}
                        </p>
                      )}
                      <div className='flex items-center gap-4 text-xs text-t-tertiary mt-1'>
                        {coupon.validUntil && (
                          <span>
                            {t('coupons.valid_until')}:{' '}
                            {formatDate(coupon.validUntil)}
                          </span>
                        )}
                        {coupon.maxUsesPerUser && coupon.userCoupons && (
                          <span>
                            {t('coupons.uses')}: {coupon.userCoupons.usedCount}/
                            {coupon.maxUsesPerUser}
                          </span>
                        )}
                        {coupon.userCoupons?.assignedAt && (
                          <span>
                            {t('coupons.assigned')}:{' '}
                            {formatDate(coupon.userCoupons.assignedAt)}
                          </span>
                        )}
                      </div>
                      {coupon.minimumAmount && (
                        <p className='text-xs text-t-tertiary'>
                          {t('coupons.minimum_amount')}: ${coupon.minimumAmount}
                        </p>
                      )}
                    </div>
                    <div className='flex items-center gap-2 ml-4'>
                      {coupon.type === 'CARD_CREATION' && (
                        <button
                          type='button'
                          onClick={() => handleClaimCardCreationCoupon(coupon)}
                          className='px-4 py-2 rounded-lg bg-primary-02 text-white font-medium transition-colors hover:bg-primary-01 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-01 whitespace-nowrap'
                        >
                          {t('coupons.claim_card')}
                        </button>
                      )}
                      <button
                        type='button'
                        onClick={() => copyToClipboard(coupon.code)}
                        className='p-2 rounded-lg hover:bg-b-surface3 transition-all group'
                        title={t('coupons.copy_code')}
                      >
                        <Icon
                          className='w-5 h-5 fill-t-secondary group-hover:fill-t-primary transition-colors'
                          name='copy'
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Disable to think about showing expired/used coupons */}
          {usedCoupons.length < 0 && (
            <div className='flex flex-col gap-3'>
              <h3 className='text-sm font-semibold text-t-secondary'>
                {t('coupons.used_or_expired')}
              </h3>
              {usedCoupons.map((coupon) => {
                const isExpired =
                  coupon.validUntil &&
                  new Date(coupon.validUntil) <= new Date();
                const hasReachedMaxUses =
                  coupon.maxUsesPerUser && coupon.userCoupons
                    ? coupon.userCoupons.usedCount >= coupon.maxUsesPerUser
                    : false;

                return (
                  <div
                    key={coupon.id}
                    className='flex items-center justify-between p-4 rounded-lg bg-b-surface2 border border-b-border opacity-60'
                  >
                    <div className='flex flex-col gap-1 flex-1'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <span className='font-semibold text-t-primary text-base line-through'>
                          {coupon.code}
                        </span>
                        <span className='px-2 py-0.5 text-xs font-medium rounded-full bg-t-tertiary/10 text-t-tertiary'>
                          {isExpired
                            ? t('coupons.expired')
                            : hasReachedMaxUses
                              ? t('coupons.no_uses_available')
                              : t('coupons.inactive')}
                        </span>
                      </div>
                      <p className='text-sm text-t-primary font-medium'>
                        {coupon.name}
                      </p>
                      {coupon.userCoupons?.lastUsedAt && (
                        <p className='text-xs text-t-tertiary'>
                          {t('coupons.last_used')}:{' '}
                          {formatDate(coupon.userCoupons.lastUsedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {coupons.length === 0 && (
            <div className='flex flex-col items-center justify-center py-8 text-center'>
              <Icon className='w-12 h-12 fill-t-tertiary mb-3' name='promote' />
              <p className='text-t-secondary text-sm'>
                {t('coupons.no_coupons')}
              </p>
              <p className='text-t-tertiary text-xs mt-1'>
                {t('coupons.coupons_appear')}
              </p>
            </div>
          )}
        </div>
      </Card>

      <Modal
        open={isModalCreateCardOpen}
        onClose={() => setIsModalCreateCardOpen(false)}
      >
        <CreateNewCard
          onClose={() => setIsModalCreateCardOpen(false)}
          cardTypeData={selectedCardType}
        />
      </Modal>
      <Modal
        open={isModalOpenCreateCardHolder}
        onClose={() => setIsModalOpenCreateCardHolder(false)}
      >
        <CreateNewCardHolder
          onClose={() => setIsModalOpenCreateCardHolder(false)}
          cardTypeData={selectedCardType}
        />
      </Modal>
    </>
  );
};

export default Coupons;
